/**
 * Shared modal for raising a support request.
 *
 * Same UI for installers and staff — the caller flips `isStaff` so staff-only
 * bits (assignee dropdown, "log on behalf of") appear. Installers now:
 *   • pick their own urgency level
 *   • must pick a support category (Delivery / Installation Support / Training)
 *
 * Contact details for installer-raised requests come from the user account
 * automatically (backend denormalises them onto the ticket at creation), so
 * staff on the Support Board never have to hunt through profiles for a phone
 * number.
 *
 * Attachments upload via /api/support/tickets/:id/attachments one file at a
 * time, after the request itself is created, so a single upload failure
 * never blocks the request getting logged.
 */
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Paperclip, Loader2, Flame, Zap, Clock, Trash2, UserCircle2, Truck, Hammer, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';

const PRIORITY_OPTIONS = [
  { key: 'standard', label: 'Standard', icon: Clock, ring: 'ring-slate-400' },
  { key: 'priority', label: 'Priority', icon: Zap, ring: 'ring-amber-400' },
  { key: 'urgent',   label: 'Urgent',   icon: Flame, ring: 'ring-rose-500' },
];

const CATEGORY_OPTIONS = [
  { key: 'delivery',             label: 'Delivery',             icon: Truck,          ring: 'ring-emerald-400', hint: 'Missing items, damage, order not received' },
  { key: 'installation_support', label: 'Installation Support', icon: Hammer,         ring: 'ring-sky-400',     hint: 'On-site question, product issue, spec advice' },
  { key: 'training_course',      label: 'Training Course',      icon: GraduationCap,  ring: 'ring-violet-400',  hint: 'Bookings, certificates, course questions' },
];

const NewSupportTicketModal = ({ onClose, onCreated, isStaff = false, initialTitle = '' }) => {
  const { user } = useContext(AuthContext);
  const [title, setTitle] = useState(initialTitle);
  const [installerName, setInstallerName] = useState('');
  const [installerPhone, setInstallerPhone] = useState('');
  const [installerEmail, setInstallerEmail] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('standard');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');

  useEffect(() => {
    if (!isStaff && user?.name) setInstallerName(user.name);
    else if (!isStaff && (user?.first_name || user?.last_name)) {
      setInstallerName(`${user.first_name || ''} ${user.last_name || ''}`.trim());
    }
  }, [user, isStaff]);

  useEffect(() => {
    // Only staff need the assignee dropdown — skip the fetch for installers.
    if (!isStaff) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/support/staff-members`, { credentials: 'include' });
        if (res.ok) setAssignees(await res.json());
      } catch (_e) { /* silent — dropdown just stays empty */ }
    })();
  }, [isStaff]);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    // Cap at 5 files, 15MB each — matches the backend limit
    const filtered = arr.filter((f) => {
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name} is over 15MB — skipped`);
        return false;
      }
      return true;
    });
    setFiles((cur) => [...cur, ...filtered].slice(0, 5));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!installerName.trim()) { toast.error('Installer name is required'); return; }
    // Category is required for installer-raised requests (product spec). Staff
    // using the board as a to-do can leave it blank.
    if (!isStaff && !category) { toast.error('Please pick what support is required'); return; }
    if (!description.trim()) { toast.error('Please describe the issue'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          installer_name: installerName.trim(),
          title: (title.trim() || (isStaff ? '' : 'Support')),
          priority,
          category: category || null,
          description: description.trim(),
          ...(isStaff && installerPhone.trim() ? { installer_phone: installerPhone.trim() } : {}),
          ...(isStaff && installerEmail.trim() ? { installer_email: installerEmail.trim() } : {}),
          ...(isStaff && assignedTo ? { assigned_to: assignedTo } : {}),
        }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Request failed to send');
      const ticket = await res.json();

      // Upload attachments one at a time so a single 502 doesn't cost all files.
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const up = await fetch(`${API_URL}/api/support/tickets/${ticket.id}/attachments`, {
          method: 'POST', credentials: 'include', body: form,
        });
        if (!up.ok) {
          const errText = formatApiError(await up.json().catch(() => ({}))) || `${f.name} failed to upload`;
          toast.warning(errText);
        }
      }
      toast.success(`Request ${ticket.reference} sent`);
      onCreated?.(ticket);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="new-support-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="font-bold text-lg">Request Support</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <Label>What support is required?{!isStaff && <span className="text-rose-600"> *</span>}</Label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {CATEGORY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = category === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setCategory(opt.key)}
                    data-testid={`request-category-${opt.key}`}
                    title={opt.hint}
                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-lg border text-xs font-semibold transition ${
                      active ? `ring-2 ${opt.ring} bg-slate-50 border-slate-300` : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-center leading-tight">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {isStaff && (
              <p className="text-[11px] text-slate-500 mt-1">
                Optional for staff-raised requests. Leave blank when using the board as a to-do list.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="ticket-title">Title (optional)</Label>
            <Input
              id="ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isStaff ? 'e.g. Delivery Check, Callback' : 'A short summary'}
              data-testid="ticket-title"
              maxLength={200}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="installer-name">Installer name</Label>
            <Input
              id="installer-name"
              value={installerName}
              onChange={(e) => setInstallerName(e.target.value)}
              placeholder="e.g. Bright Resin Ltd"
              disabled={!isStaff && !!user?.name}
              data-testid="ticket-installer-name"
              className="mt-1"
              required
            />
          </div>

          {isStaff && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="installer-phone">Contact phone</Label>
                <Input
                  id="installer-phone"
                  value={installerPhone}
                  onChange={(e) => setInstallerPhone(e.target.value)}
                  placeholder="Optional"
                  data-testid="ticket-installer-phone"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="installer-email">Contact email</Label>
                <Input
                  id="installer-email"
                  type="email"
                  value={installerEmail}
                  onChange={(e) => setInstallerEmail(e.target.value)}
                  placeholder="Optional"
                  data-testid="ticket-installer-email"
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <div>
            <Label>Urgency level</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {PRIORITY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = priority === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setPriority(opt.key)}
                    data-testid={`ticket-priority-${opt.key}`}
                    className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-semibold transition ${
                      active ? `ring-2 ${opt.ring} bg-slate-50 border-slate-300` : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="ticket-description">Describe the issue</Label>
            <Textarea
              id="ticket-description"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What went wrong? Include any relevant order/product references."
              data-testid="ticket-description"
              className="mt-1"
              required
            />
          </div>

          {isStaff && (
            <div>
              <Label htmlFor="ticket-assign">
                <UserCircle2 className="w-4 h-4 inline mr-1 -mt-0.5" /> Assign to
              </Label>
              <select
                id="ticket-assign"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                data-testid="ticket-assign"
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">— Unassigned —</option>
                {assignees.map((s) => (
                  <option key={s.user_id} value={s.user_id}>{s.name} ({s.role})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label>Photos or attachments (optional, max 5 files, 15MB each)</Label>
            <div className="mt-1 flex items-center gap-2">
              <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-slate-50 text-sm" data-testid="ticket-attach">
                <Paperclip className="w-4 h-4" /> Choose files
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </label>
              <span className="text-xs text-slate-500">{files.length}/5 selected</span>
            </div>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={`${f.name}-${f.size}-${f.lastModified || 0}`} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span className="truncate">{f.name} <span className="text-slate-400">· {(f.size / 1024 / 1024).toFixed(1)}MB</span></span>
                    <button
                      type="button"
                      onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
                      className="text-slate-400 hover:text-rose-600 ml-2"
                      aria-label={`Remove ${f.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="ticket-submit" className="bg-slate-900 hover:bg-slate-800">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</> : 'Send Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewSupportTicketModal;
